import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getRecentCriticalEvents } from '../../../../utils/services/eventService';
import { showErrorAlert } from '../../../../utils/alerts';
import { resolveIncident, startIncident } from '../../../../utils/services/eventIncident';
import '../../../../assets/css/recentCriticalEventsMonitor.css';
import moment from 'moment-timezone';

moment.locale('es');

const RecentCriticalEventsMonitor = () => {
  const [recentCriticalEvents, setRecentCriticalEvents] = useState([]);
  const [solutionComment, setSolutionComment] = useState('');

  const getRecentCriticalEventsMonitor = async () => {
    try {
      const response = await getRecentCriticalEvents();
      setRecentCriticalEvents(response);
    } catch (error) {
      console.error('Error al obtener los eventos críticos más recientes:', error);
      showErrorAlert('Error', error.message || 'No se pudieron obtener los eventos críticos más recientes.');
    }
  };

  const handleStartIncident = async (event) => {
    try {
      await startIncident({
        eventId: event.id,
        supportUserId: localStorage.getItem("userId"),
        supportUserName: localStorage.getItem("userName"),
        supportReason: `${event.buildingName}: ${getEvent(event.type)} Por ${event.userName} del apartamento ${event.apartment}.`,
      });
      getRecentCriticalEventsMonitor();
    } catch (error) {
      console.error("Error al iniciar el soporte:", error);
      showErrorAlert('Error', 'No se pudo iniciar el soporte.');
    }
  };

  const handleResolveIncident = async (eventId) => {
    try {
      await resolveIncident({ eventId, solutionComment });
      setSolutionComment('');
      getRecentCriticalEventsMonitor();
    } catch (error) {
      showErrorAlert('Error', 'No se pudo cerrar el soporte.');
    }
  };

  const getEvent = (eventType) => {
    switch (eventType) {
      case 'EMERGENCY':
      case 'PUSH_BUTTON':
        return 'Emergencia activada.';
      case 'INTRUSION':
        return 'Intrusión activada.';
      case 'REMOTE_PANIC':
        return 'Botón de pánico presionado.';
      case 'LONG_OPEN_DOOR':
        return 'Puerta abierta +2 minutos.';
      case 'FORCED':
        return 'Puerta forzada.';
      case 'DISCONNECTED_BUILDING':
        return 'Edificio desconectado.';
      default:
        return 'Evento desconocido.';
    }
  };

  const getStatus = (status) => {
    switch (status) {
      case 'DONE':
        return 'Finalizado.';
      case 'IN_PROGRESS':
        return 'En progreso.';
      default:
        return 'Pendiente.';
    }
  };

  useEffect(() => {
    getRecentCriticalEventsMonitor();
  }, []);

  return (
    <div className="recent-events-container">
      <div className="recent-events-header">
        <h2>Eventos críticos recientes</h2>
        <button className="refresh-button" onClick={getRecentCriticalEventsMonitor}>
          <span className="material-icons">refresh</span>
        </button>
      </div>

      <div className="recent-events-list">
        {recentCriticalEvents.map((event) => {
          const {
            id,
            buildingName,
            type,
            door,
            userName,
            userPhone,
            createdAt,
            apartment,
            origin,
            buildingId,
            incidentId,
            supportUserName,
            status,
            solutionComment,
            incidentUpdatedAt,
          } = event;

          const formattedDate = moment.tz(createdAt, 'UTC').tz('America/Bogota').format('D MMM YYYY h:mm A');

          return (
            <div key={id} className="recent-event-card">
              <div className="d-flex justify-content-around align-items-center">
                <span>{formattedDate}</span>
                <Link
                  to={`/admin-dashboard/buildings/${buildingId}`}
                  className="view-building-btn"
                >
                  <span className="material-icons">visibility</span>
                </Link>
              </div>
              <strong>{buildingName}:</strong> {getEvent(type)}
              {door && (
                <>
                  <strong> Puerta:</strong> {door}{' '}
                </>
              )}
              <br />

              {origin === 'APP' && userName && (
                <>
                  <strong>Por:</strong> {userName} <br />
                  {apartment && (
                    <>
                      <strong>Apartamento:</strong> {apartment} <br />
                    </>
                  )}

                  {status !== 'DONE' && status !== 'IN_PROGRESS' && (
                    <>
                      <a
                        href={`https://wa.me/${userPhone}?text=${encodeURIComponent(
                          'Hola, soporte técnico está atendiendo tu reporte. ¿Puedes explicarnos lo que sucede, por favor?'
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="contact-resident-link"
                        onClick={() => handleStartIncident(event)}
                      >
                        Contactar residente
                      </a>
                      <br />
                    </>
                  )}
                </>
              )}
              <strong className="support-label">Soporte:</strong> {getStatus(status)} <br />
              {origin === 'PHYSICAL' && status !== 'DONE' && status !== 'IN_PROGRESS' && (
                <button
                  onClick={() => handleStartIncident(event)}
                  className="start-support-btn"
                >
                  Iniciar soporte
                </button>
              )}

              {incidentId && (
                <div className="incident-info">
                  {status === 'IN_PROGRESS' ? (
                    <div className="resolve-section">
                      <strong>Por: </strong>{supportUserName}. <br />
                      <strong>Última actualización:</strong>{' '}
                      {incidentUpdatedAt && moment(incidentUpdatedAt).format('D MMM YYYY h:mm A')}
                      <textarea
                        value={solutionComment}
                        onChange={(e) => setSolutionComment(e.target.value)}
                        placeholder="Escribe la solución..."
                        className="solution-textarea"
                        required
                      />
                      <button
                        onClick={() => handleResolveIncident(id)}
                        className="close-support-btn"
                      >
                        Cerrar soporte
                      </button>
                    </div>
                  ) : status === 'DONE' ? (
                    <div className="resolve-section">
                      <strong>Por: </strong>{supportUserName}. <br />
                      <strong>Última actualización:</strong>{' '}
                      {incidentUpdatedAt && moment(incidentUpdatedAt).format('D MMM YYYY h:mm A')}
                      <br />
                      <strong>Solución:</strong> {solutionComment}
                    </div>
                  ) : (
                    <>
                      {userPhone && (
                        <a
                          href={`https://wa.me/${userPhone}?text=${encodeURIComponent(
                            'Hola, soporte técnico está atendiendo tu reporte. ¿Puedes explicarnos lo que sucede, por favor?'
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="contact-resident-link"
                          onClick={() => handleStartIncident(event)}
                        >
                          Contactar residente
                        </a>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RecentCriticalEventsMonitor;
